import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import MuiTooltip from '@material-ui/core/Tooltip';
import { makeStyles, withStyles } from '@material-ui/core/styles';

import { capitalize } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/utils';

export const COLORS = [
  'ant',
  'primary',
  'accentBlue',
  'accentBlue2',
  'accentGreen',
  'orange',
  'secondary',
];

const styles = () => ({
  popper: {},
  popperInteractive: {},
  popperArrow: {},
  tooltip: {},
  tooltipArrow: {},
  arrow: {},
  touch: {},
  tooltipPlacementLeft: {},
  tooltipPlacementRight: {},
  tooltipPlacementTop: {},
  tooltipPlacementBottom: {},
});

const getArrowColor = (color) => {
  switch (color) {
    case 'ant':
      return {
        color: '#E9E9E9', // Not using a theme color because I need to match Antd styling,
      };
    default:
      return {
        color,
      };
  }
};

const getTooltipColor = (theme, color) => {
  switch (color) {
    case 'ant':
      return {
        ...theme.typography.h6,
        padding: theme.spacing(0.75, 1),
        backgroundColor: '#E9E9E9', // Not using a theme color because I need to match Antd styling.
        color: '#595959', // Not using a theme color because I need to match Antd styling.
      };
    default:
      return {
        color: theme.palette.common.white,
        backgroundColor: color,
      };
  }
};

const useCustomStyles = makeStyles(theme => ({
  // Arrow color
  arrowColorAnt: getArrowColor('ant'),
  arrowColorAccentBlue: getArrowColor(theme.palette.facet.accentBlue),
  arrowColorAccentBlue2: getArrowColor(theme.palette.facet.accentBlue2),
  arrowColorDarkGray: getArrowColor(theme.palette.facet.darkGray),
  arrowColorLightGray: getArrowColor(theme.palette.facet.lightGray),
  arrowColorPrimaryGray: getArrowColor(theme.palette.facet.primaryGray),
  arrowColorRed: getArrowColor(theme.palette.facet.red),
  arrowColorOrange: getArrowColor(theme.palette.facet.orange),
  arrowColorSeafoam: getArrowColor(theme.palette.facet.seafoam),
  // Tooltip color
  tooltipColorAnt: getTooltipColor(theme, 'ant'),
  tooltipColorAccentBlue: getTooltipColor(theme, theme.palette.facet.accentBlue),
  tooltipColorAccentBlue2: getTooltipColor(theme, theme.palette.facet.accentBlue2),
  tooltipColorDarkGray: getTooltipColor(theme, theme.palette.facet.darkGray),
  tooltipColorLightGray: getTooltipColor(theme, theme.palette.facet.lightGray),
  tooltipColorPrimaryGray: getTooltipColor(theme, theme.palette.facet.primaryGray),
  tooltipColorRed: getTooltipColor(theme, theme.palette.facet.red),
  tooltipColorOrange: getTooltipColor(theme, theme.palette.facet.orange),
  tooltipColorSeafoam: getTooltipColor(theme, theme.palette.facet.seafoam),
  // Other custom classes
  tooltipRounded: {
    borderRadius: theme.radius.md,
  },
}));

function StyledTooltip({
  className,
  classes,
  color,
  variant,
  ...rest
}) {
  const {
    arrow: arrowClass,
    tooltip: tooltipClass,
    ...restClasses
  } = classes;

  const customClasses = useCustomStyles();

  return (
    <MuiTooltip
      classes={{
        ...restClasses,
        root: classNames(classes.root, className),
        arrow: classNames(arrowClass, customClasses[`arrowColor${capitalize(color)}`]),
        tooltip: classNames(tooltipClass, customClasses[`tooltipColor${capitalize(color)}`], {
          [customClasses.tooltipRounded]: variant === 'rounded',
        }),
      }}
      {...rest}
    />
  );
}

StyledTooltip.defaultProps = {
  className: '',
  color: 'ant',
  variant: 'default',
};

StyledTooltip.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  color: PropTypes.oneOf(COLORS),
  variant: PropTypes.oneOf([
    'default',
    'rounded',
  ]),
};

export default withStyles(styles)(StyledTooltip);
